import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';

import { Button, Spinner } from 'modules/v2/common/AtomicDesign/atoms';
import {
  getModCoverLetterTemplates,
  getModMailingBranding,
  updateModMailingBranding,
  uploadModBrandingImage,
} from 'modules/api/digitalServices/mailOnDemand';
import { cn } from 'modules/v2/common/utils/cn';

import strings, { formatMessage } from '../../strings';
import CoverLetterPreview from './CoverLetterPreview';

const REQUIRED_BRANDING = ['agentName', 'email', 'headshotUrl', 'companyName'];

const brandingMissing = (branding) =>
  REQUIRED_BRANDING.filter((field) => !branding || !String(branding[field] || '').trim());

const TemplateCard = ({ tpl, selected, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(tpl.key)}
    data-testid={`cl-template-${tpl.key}`}
    className={cn(
      'flex-1 rounded-xl border bg-white p-3 text-left transition-colors',
      selected ? 'border-primary-500 ring-2 ring-primary-200' : 'border-neutral-200 hover:border-neutral-400',
    )}
  >
    <div className="flex items-center justify-between">
      <span className="text-sm font-semibold">
        {tpl.key === 'modern' ? strings.coverLetter.templateModern : strings.coverLetter.templateClassic}
      </span>
      {selected && (
        <span className="grid h-4 w-4 place-items-center rounded-full bg-primary-500 text-[10px] text-white">✓</span>
      )}
    </div>
    <span className="text-xs text-neutral-500">
      {tpl.key === 'modern' ? strings.coverLetter.templateModernHint : strings.coverLetter.templateClassicHint}
    </span>
  </button>
);

const BrandingField = ({ label, value, onChange, type = 'text', placeholder }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-semibold text-neutral-600">{label}</span>
    <input
      type={type}
      value={value || ''}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
    />
  </label>
);

const ImageUpload = ({ label, optional, hint, url, uploading, onUpload, onRemove, testId }) => {
  const inputRef = useRef(null);

  return (
    <div className="flex items-center gap-3 rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-3">
      <div className="grid h-12 w-12 flex-none place-items-center overflow-hidden rounded-lg bg-neutral-200">
        {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : <span className="text-neutral-400">▦</span>}
      </div>
      <div className="flex-1">
        <div className="text-sm font-semibold">
          {label}
          {optional && (
            <span className="ml-2 rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] font-semibold text-neutral-500">
              {strings.coverLetter.brandingOptional}
            </span>
          )}
        </div>
        {hint && <div className="text-xs text-neutral-500">{hint}</div>}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid={testId}
        onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])}
      />
      <Button color="outlined" disabled={uploading} onClick={() => inputRef.current?.click()}>
        {uploading ? strings.coverLetter.brandingUploading : strings.coverLetter.brandingUpload}
      </Button>
      {url && onRemove && (
        <Button color="bare" disabled={uploading} onClick={onRemove} data-testid={`${testId}-remove`}>
          {strings.coverLetter.brandingRemoveImage}
        </Button>
      )}
    </div>
  );
};

const CoverLetterEditor = ({ bookTitle, value, onSave, onCancel, onRemove }) => {
  const templatesQuery = useQuery('modCoverLetterTemplates', getModCoverLetterTemplates);
  const brandingQuery = useQuery('modMailingBranding', getModMailingBranding);

  const [templateKey, setTemplateKey] = useState(value?.templateKey || 'classic');
  const [body, setBody] = useState(value?.editedBody || '');
  const [bodyDirty, setBodyDirty] = useState(Boolean(value?.editedBody));
  const [branding, setBranding] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [saving, setSaving] = useState(false);

  // Make the live preview fill the right pane (bounded) instead of a fixed 408px.
  const previewPaneRef = useRef(null);
  const [previewWidth, setPreviewWidth] = useState(408);
  useEffect(() => {
    const el = previewPaneRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const w = Math.max(360, Math.min(560, Math.floor(entry.contentRect.width)));
      setPreviewWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const templates = useMemo(() => templatesQuery.data || [], [templatesQuery.data]);
  const selectedTemplate = useMemo(
    () => templates.find((t) => t.key === templateKey),
    [templates, templateKey],
  );

  // Seed the body from the selected template's default until the member edits it.
  useEffect(() => {
    if (!bodyDirty && selectedTemplate?.bodyDefault) {
      setBody(selectedTemplate.bodyDefault);
    }
  }, [bodyDirty, selectedTemplate]);

  // Initialize the editable branding from the prefilled profile once.
  useEffect(() => {
    if (brandingQuery.data && !branding) {
      setBranding(brandingQuery.data);
    }
  }, [brandingQuery.data, branding]);

  const setBrandingField = (field, val) =>
    setBranding((prev) => ({ ...prev, [field]: val }));

  const handleUpload = async (kind, file) => {
    setUploading(kind);
    try {
      const updated = await uploadModBrandingImage(kind, file);
      setBranding((prev) => ({ ...prev, ...updated }));
    } catch (error) {
      // surfaced via the missing-field banner staying visible
    } finally {
      setUploading(null);
    }
  };

  // Clear an uploaded image. Optimistic; `null` tells the backend to clear the
  // stored URL (and stop re-prefilling it from BBA/customer).
  const handleRemoveImage = async (kind) => {
    const field = kind === 'headshot' ? 'headshotUrl' : 'brokerLogoUrl';
    setBranding((prev) => ({ ...prev, [field]: '' }));
    try {
      await updateModMailingBranding({ [field]: null });
    } catch (error) {
      // keep the cleared optimistic state
    }
  };

  const missing = brandingMissing(branding);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateModMailingBranding({
        agentName: branding?.agentName,
        companyName: branding?.companyName,
        phone: branding?.phone,
        email: branding?.email,
      });
      onSave({ templateKey, editedBody: body });
    } catch (error) {
      // keep the editor open; a generic save failure leaves state intact
    } finally {
      setSaving(false);
    }
  };

  if (templatesQuery.isLoading || brandingQuery.isLoading) {
    return (
      <div className="flex flex-col items-center gap-3 py-12">
        <Spinner />
      </div>
    );
  }

  if (templatesQuery.isError) {
    return (
      <div className="py-8">
        <p className="text-neutral-700">{strings.coverLetter.unavailable}</p>
        <Button color="outlined" className="mt-3" onClick={onCancel}>
          {strings.coverLetter.backToAssets}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold">{strings.coverLetter.editorTitle}</h2>
          <p className="text-sm text-neutral-500">
            {formatMessage(strings.coverLetter.editorSubtitle, { bookTitle })}
          </p>
        </div>
        <span className="rounded-full bg-success-50 px-3 py-1 text-xs font-semibold text-success-700">
          ✓ {strings.coverLetter.prefilledChip}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[440px_1fr]">
        {/* LEFT — controls */}
        <div className="rounded-xl border border-neutral-200 bg-white p-4">
          <div className="mb-2 text-xs font-bold text-neutral-600">{strings.coverLetter.templateLabel}</div>
          <div className="flex gap-3">
            {templates.map((tpl) => (
              <TemplateCard key={tpl.key} tpl={tpl} selected={tpl.key === templateKey} onSelect={setTemplateKey} />
            ))}
          </div>

          <div className="mt-5 border-t border-neutral-100 pt-4">
            <div className="mb-2 text-xs font-bold text-neutral-600">{strings.coverLetter.messageLabel}</div>
            <textarea
              value={body}
              rows={7}
              data-testid="cl-body"
              onChange={(e) => {
                setBody(e.target.value);
                setBodyDirty(true);
              }}
              className="w-full rounded-lg border border-neutral-300 p-3 text-sm leading-relaxed focus:border-primary-500 focus:outline-none"
            />
            <p className="mt-2 text-xs text-neutral-500">ⓘ {strings.coverLetter.messageHint}</p>
          </div>

          <div className="mt-5 border-t border-neutral-100 pt-4">
            <div className="mb-3 flex items-center gap-2 text-xs font-bold text-neutral-600">
              {strings.coverLetter.brandingLabel}
              <span className="rounded bg-neutral-100 px-2 py-0.5 text-[10px] font-medium text-neutral-500">
                {strings.coverLetter.brandingAutoFilled}
              </span>
            </div>

            {missing.length > 0 && (
              <div className="mb-3 rounded-lg bg-amber-50 p-3" data-testid="cl-branding-missing">
                <div className="text-xs font-semibold text-amber-800">{strings.coverLetter.brandingMissingTitle}</div>
                <div className="text-xs text-amber-700">{strings.coverLetter.brandingMissingBody}</div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <BrandingField label={strings.coverLetter.brandingName} value={branding?.agentName} onChange={(v) => setBrandingField('agentName', v)} />
              <BrandingField label={strings.coverLetter.brandingCompany} value={branding?.companyName} onChange={(v) => setBrandingField('companyName', v)} />
              <BrandingField label={strings.coverLetter.brandingPhone} value={branding?.phone} onChange={(v) => setBrandingField('phone', v)} />
              <BrandingField label={strings.coverLetter.brandingEmail} type="email" value={branding?.email} onChange={(v) => setBrandingField('email', v)} />
            </div>

            <div className="mt-3 space-y-3">
              <ImageUpload
                label={strings.coverLetter.brandingHeadshot}
                url={branding?.headshotUrl}
                uploading={uploading === 'headshot'}
                onUpload={(file) => handleUpload('headshot', file)}
                onRemove={() => handleRemoveImage('headshot')}
                testId="cl-upload-headshot"
              />
              <ImageUpload
                label={strings.coverLetter.brandingBrokerLogo}
                optional
                hint={strings.coverLetter.brandingBrokerLogoHint}
                url={branding?.brokerLogoUrl}
                uploading={uploading === 'broker-logo'}
                onUpload={(file) => handleUpload('broker-logo', file)}
                onRemove={() => handleRemoveImage('broker-logo')}
                testId="cl-upload-broker-logo"
              />
            </div>
          </div>
        </div>

        {/* RIGHT — live preview */}
        <div className="rounded-xl border border-neutral-200 bg-white p-4">
          <div className="mb-4 flex items-center justify-between">
            <span className="text-sm font-semibold">{strings.coverLetter.livePreview}</span>
            <span className="rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700">
              ★ {strings.coverLetter.personalizedPerRecipient}
            </span>
          </div>
          <div ref={previewPaneRef} className="flex justify-center rounded-xl bg-neutral-100 p-4">
            <CoverLetterPreview
              templateKey={templateKey}
              body={body}
              branding={branding || {}}
              leadName={strings.coverLetter.sampleLeadName}
              width={previewWidth}
            />
          </div>
          <p className="mt-3 text-center text-xs text-neutral-500">{strings.coverLetter.previewRecipientsNote}</p>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between border-t border-neutral-100 pt-4">
        <div className="flex gap-3">
          <Button color="outlined" onClick={onCancel}>{strings.coverLetter.backToAssets}</Button>
          {value && (
            <Button color="dangerOutlined" onClick={onRemove} data-testid="cl-remove">
              {strings.coverLetter.remove}
            </Button>
          )}
        </div>
        <Button onClick={handleSave} disabled={saving || missing.length > 0} data-testid="cl-save">
          {strings.coverLetter.saveAndContinue}
        </Button>
      </div>
    </div>
  );
};

export default CoverLetterEditor;
